import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import AppShell from './components/layout/AppShell'
import PublicShell from './components/layout/PublicShell'
import Spinner from './components/ui/Spinner'
import { useAuthStore } from './store/authStore'
import { AuthProvider } from './features/auth/AuthContext'
import ToastContainer from './components/ui/Toast'
import MaintenanceGate from './components/common/MaintenanceGate'
import ProtectedRouteGuard from './components/ProtectedRoute'
import ScrollToTop from './components/ScrollToTop'
import ServerMaintenanceOverlay from './components/common/ServerMaintenanceOverlay'
import { useBackendHealth } from './hooks/useBackendHealth'

// 코드 스플리팅 — 초기 번들 최소화
const Landing = lazy(() => import('./pages/public/Landing'))
const Pricing = lazy(() => import('./pages/public/Pricing'))
const About = lazy(() => import('./pages/public/About'))
const Contact = lazy(() => import('./pages/public/Contact'))
const Privacy = lazy(() => import('./pages/public/Privacy'))
const Terms = lazy(() => import('./pages/public/Terms'))
const NotFound = lazy(() => import('./pages/public/NotFound'))
const OnPremise = lazy(() => import('./pages/public/OnPremise'))
const AcDataCenter = lazy(() => import('./pages/ac-datacenter/AcDataCenter'))
const AcArchive = lazy(() => import('./pages/ac-datacenter/AcArchive'))
const AcCategory = lazy(() => import('./pages/ac-datacenter/AcCategory'))
const InsuranceRefund = lazy(() => import('./pages/insurance-refund/InsuranceRefund'))
const InsuranceRefundIndustry = lazy(() => import('./pages/insurance-refund/InsuranceRefundIndustry'))
const Blog = lazy(() => import('./pages/blog/Blog'))
const BlogPost = lazy(() => import('./pages/blog/BlogPost'))

// ── /features/* 9개 SEO 공개 마케팅 랜딩 (인증 불필요) ──────────────
const FeatureJSA              = lazy(() => import('./pages/public/features').then(m => ({ default: m.FeatureJSA })))
const FeatureTBM              = lazy(() => import('./pages/public/features').then(m => ({ default: m.FeatureTBM })))
const FeatureSafetyEducation  = lazy(() => import('./pages/public/features').then(m => ({ default: m.FeatureSafetyEducation })))
const FeatureWorkPermit       = lazy(() => import('./pages/public/features').then(m => ({ default: m.FeatureWorkPermit })))
const FeatureAccidentReport   = lazy(() => import('./pages/public/features').then(m => ({ default: m.FeatureAccidentReport })))
const FeatureChatbot          = lazy(() => import('./pages/public/features').then(m => ({ default: m.FeatureChatbot })))
const FeatureRiskAssessment   = lazy(() => import('./pages/public/features').then(m => ({ default: m.FeatureRiskAssessment })))
const FeatureImageAnalysis    = lazy(() => import('./pages/public/features').then(m => ({ default: m.FeatureImageAnalysis })))
const FeatureCorrective       = lazy(() => import('./pages/public/features').then(m => ({ default: m.FeatureCorrective })))
const FeatureETA              = lazy(() => import('./pages/public/features').then(m => ({ default: m.FeatureETA })))
const FeatureFTA              = lazy(() => import('./pages/public/features').then(m => ({ default: m.FeatureFTA })))
const FeatureSafetyCost       = lazy(() => import('./pages/public/features').then(m => ({ default: m.FeatureSafetyCost })))
const FeaturePnidHazop        = lazy(() => import('./pages/public/FeaturePnidHazop'))
const Newsletter              = lazy(() => import('./pages/public/Newsletter'))
const NewsletterVerify        = lazy(() => import('./pages/public/NewsletterVerify'))
const NewsletterUnsubscribe   = lazy(() => import('./pages/public/NewsletterUnsubscribe'))

const Login = lazy(() => import('./pages/auth/Login'))
const Signup = lazy(() => import('./pages/auth/Signup'))
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'))
const OAuthCallback = lazy(() => import('./pages/auth/OAuthCallback'))

const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'))
const Usage     = lazy(() => import('./pages/dashboard/Usage'))
const DailyBriefToday   = lazy(() => import('./pages/daily-brief/Today'))
const DailyBriefArchive = lazy(() => import('./pages/daily-brief/Archive'))
const Chatbot = lazy(() => import('./pages/chatbot/Chatbot'))
const ChatStudio = lazy(() => import('./pages/chat-studio/ChatStudio'))
const Integrated = lazy(() => import('./pages/integrated/Integrated'))
const JSA = lazy(() => import('./pages/jsa/JSA'))
const TBM = lazy(() => import('./pages/tbm/TBM'))
const ImageAnalysis = lazy(() => import('./pages/image-analysis/ImageAnalysis'))
const Corrective = lazy(() => import('./pages/corrective/Corrective'))
const RiskAssessment = lazy(() => import('./pages/risk-assessment/RiskAssessment'))
const WorkPermit = lazy(() => import('./pages/work-permit/WorkPermit'))
const AccidentReport = lazy(() => import('./pages/accident-report/AccidentReport'))
const SafetyEducation = lazy(() => import('./pages/safety-education/SafetyEducation'))
const SafetyEducationCourse = lazy(() => import('./pages/safety-education/SafetyEducationCourse'))
const CourseAttendanceSummary = lazy(() => import('./pages/safety-education/CourseAttendanceSummary'))
const ETA = lazy(() => import('./pages/eta/ETA'))
const FTA = lazy(() => import('./pages/fta/FTA'))
// 방폭 폭발위험장소 구분 — URL 전용(사이드바 미노출), 테스트 단계
const HazardAreaClassification = lazy(() => import('./pages/hazard-area-classification/HazardAreaClassification'))
const EquipmentCert = lazy(() => import('./pages/equipment-cert/EquipmentCert'))
const SafetyCost = lazy(() => import('./pages/safety-cost/SafetyCost'))
const SafetySigns = lazy(() => import('./pages/safety-signs/SafetySigns'))
const PnID = lazy(() => import('./pages/pnid/PnID'))
const Templates = lazy(() => import('./pages/templates/Templates'))
const Records = lazy(() => import('./pages/dashboard/Records'))
const Subscription = lazy(() => import('./pages/billing/Subscription'))
const Invoices = lazy(() => import('./pages/billing/Invoices'))
const Settings = lazy(() => import('./pages/settings/Settings'))

// 관리자 패널 페이지
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'))
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'))
const AdminUserDetail = lazy(() => import('./pages/admin/AdminUserDetail'))
const AdminSubscriptions = lazy(() => import('./pages/admin/AdminSubscriptions'))
const AdminPayments = lazy(() => import('./pages/admin/AdminPayments'))
const AdminAudit = lazy(() => import('./pages/admin/AdminAudit'))
const AdminMetrics = lazy(() => import('./pages/admin/AdminMetrics'))
const AdminActivity = lazy(() => import('./pages/admin/AdminActivity'))
const AdminSupport = lazy(() => import('./pages/admin/AdminSupport'))
const AdminNewsletter = lazy(() => import('./pages/admin/AdminNewsletter'))
const AdminSites = lazy(() => import('./pages/admin/AdminSites'))
const AdminErrorLogs = lazy(() => import('./pages/admin/AdminErrorLogs'))
const PsmLayout = lazy(() => import('./pages/admin/psm/PsmLayout'))
const PsmFieldScoreMode = lazy(() => import('./pages/admin/psm/FieldScoreMode'))
const PsmFieldPhotoMode = lazy(() => import('./pages/admin/psm/FieldPhotoMode'))
const PsmGradeResult = lazy(() => import('./pages/admin/psm/GradeResult'))
const PsmFilingSession = lazy(() => import('./pages/admin/psm/FilingSession'))
// AI 면담(설계서 §6) — 준비(감사반장) / 진행(태블릿 위임) / 종료 후 일괄 확인
const PsmInterviewPrep = lazy(() => import('./pages/admin/psm/InterviewPrep'))
const PsmInterviewRun = lazy(() => import('./pages/admin/psm/InterviewRun'))
const PsmInterviewReview = lazy(() => import('./pages/admin/psm/InterviewReview'))
const PsmPortalHome = lazy(() => import('./pages/psmportal/PsmPortalHome'))
const PsmPortalAudit = lazy(() => import('./pages/psmportal/PsmPortalAudit'))
const PsmSignPage = lazy(() => import('./pages/public/PsmSignPage'))
const AdminPsmAudit = lazy(() => import('./pages/admin/AdminPsmAudit'))
const AdminPsmClients = lazy(() => import('./pages/admin/AdminPsmClients'))
const PsmTeams = lazy(() => import('./pages/admin/psm/PsmTeams'))
// 첫 사용 안내·첫 감사 체크리스트(P3-19) · 마감 체크(설계서 §5-5)
const PsmGuide = lazy(() => import('./pages/admin/psm/PsmGuide'))
const PsmClosingCheck = lazy(() => import('./pages/admin/psm/ClosingCheck'))
const AdminPsmAuditDetail = lazy(() => import('./pages/admin/AdminPsmAuditDetail'))
const AdminContentMetrics = lazy(() => import('./pages/admin/AdminContentMetrics'))
const AdminSapaAnalysis = lazy(() => import('./pages/admin/AdminSapaAnalysis'))

// ── Partner Safety OS — 원하청 상생 안전관리 ──────────────────
const PartnersShell = lazy(() => import('./pages/partners/PartnersShell'))
const PartnersDashboard = lazy(() => import('./pages/partners/PartnersDashboard'))
const PartnerDuty = lazy(() => import('./pages/partners/PartnerDuty'))
const PartnerDetail = lazy(() => import('./pages/partners/PartnerDetail'))
const PartnerSettings = lazy(() => import('./pages/partners/PartnerSettings'))
const PartnerTickets = lazy(() => import('./pages/partners/TicketsBoard'))
const PartnerInspect = lazy(() => import('./pages/partners/InspectionWizard'))
const InviteAccept = lazy(() => import('./pages/partners/InviteAccept'))
const PartnerInviteLanding = lazy(() => import('./pages/partners/PartnerInviteLanding'))

// ── 사업장(A) + 의무(A) + 교육(C) — 통합 메뉴 ─────────────────
const TodayTasks = lazy(() => import('./pages/dashboard/TodayTasks'))
const SiteEntry = lazy(() => import('./pages/sites/SiteEntry'))
const SiteWizard = lazy(() => import('./pages/sites/SiteWizard'))
const SiteDetail = lazy(() => import('./pages/sites/SiteDetail'))
const OnboardingPage = lazy(() => import('./pages/onboarding/OnboardingPage'))
const SessionDetail = lazy(() => import('./pages/training/SessionDetail'))
const ImportHub = lazy(() => import('./pages/imports/ImportHub'))

// ── ETA/FTA 통합 ────────────────────────────────────────────
const RiskAnalysisHub = lazy(() => import('./pages/risk-analysis/RiskAnalysisHub'))

// 게스트 (인증 없음) — QR 출석 페이지
const GuestCheckIn = lazy(() => import('./pages/training-public/GuestCheckIn'))
// 게스트 (인증 없음) — QR 위험제보 페이지
const SafetyReportSubmit = lazy(() => import('./pages/public/SafetyReportSubmit'))
const CustomFormatRequest = lazy(() => import('./pages/enterprise/CustomFormatRequest'))
const SafetyRuleMatch = lazy(() => import('./pages/safety-rule-match/SafetyRuleMatch'))
const ComplianceLedger = lazy(() => import('./pages/safety-rule-match/Ledger'))
const ComplianceCheckDetail = lazy(() => import('./pages/safety-rule-match/CheckDetail'))
const WorkTiming = lazy(() => import('./pages/work-timing/WorkTiming'))
const SlideshowPlayer = lazy(() => import('./pages/safety-education/SlideshowPlayer'))
const LessonLibrary = lazy(() => import('./pages/safety-education/LessonLibrary'))

// 로딩 폴백
const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center min-h-[400px]">
    <Spinner size="lg" />
  </div>
)

// 앱 루트 — 인증 초기화 + Toast 컨테이너 + 라우트 전환마다 스크롤 복원 + 백엔드 헬스 감시
function AppRoot() {
  const { isDown, lastOkAt } = useBackendHealth()
  return (
    <AuthProvider>
      <ScrollToTop />
      <MaintenanceGate>
        <Outlet />
      </MaintenanceGate>
      <ToastContainer />
      <ServerMaintenanceOverlay isDown={isDown} lastOkAt={lastOkAt} />
    </AuthProvider>
  )
}

// 인증 필요 라우트 가드 — ProtectedRouteGuard 사용 (이메일 미인증 처리 포함)
// ProtectedRouteGuard는 src/components/ProtectedRoute.tsx 에서 임포트

// 어드민 가드 — User.is_admin 또는 role==='admin' 모두 허용
/** PSM 자체감사 가드 — 서버 psm_access_allowed 와 동일 조건(/auth/me 의 psm_access). */
function PsmRoute() {
  const { user } = useAuthStore()
  if (!user?.psm_access) return <Navigate to="/dashboard" replace />
  return (
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  )
}

function AdminRoute() {
  const { user } = useAuthStore()
  const isAdmin = user?.is_admin === true || user?.is_admin
  if (!isAdmin) return <Navigate to="/dashboard" replace />
  return (
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  )
}

export const router = createBrowserRouter([
  {
    // 최상위 — AuthProvider + Toast 공통 제공
    element: <AppRoot />,
    children: [
      // 공개 마케팅 페이지
      {
        element: (
          <PublicShell>
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </PublicShell>
        ),
        children: [
          { path: '/', element: <Landing /> },
          { path: '/pricing', element: <Pricing /> },
          { path: '/about', element: <About /> },
          { path: '/contact', element: <Contact /> },
          { path: '/privacy', element: <Privacy /> },
          { path: '/terms', element: <Terms /> },
          { path: '/on-premise', element: <OnPremise /> },
          { path: '/ac-datacenter', element: <AcDataCenter /> },
          { path: '/ac_datacenter', element: <AcDataCenter /> },
          { path: '/ac-datacenter/w/:slug', element: <AcArchive /> },
          // 사고유형(t)·기인물(a) 카테고리 SEO 페이지
          { path: '/ac-datacenter/t/:slug', element: <AcCategory axis="type" /> },
          { path: '/ac-datacenter/a/:slug', element: <AcCategory axis="agent" /> },
          // 업종·공정(i) + 교차(업종 × 재해형태) — 롱테일 검색("제조업 끼임 사고 사례") 대응
          { path: '/ac-datacenter/i/:slug', element: <AcCategory axis="industry" /> },
          { path: '/ac-datacenter/i/:industry/t/:type', element: <AcCategory axis="cross" /> },
          // 영어 SEO 전용 라우트 — /en/ 경로는 강제 영어 렌더(프리렌더 대상)
          { path: '/en/ac-datacenter', element: <AcDataCenter /> },
          { path: '/en/ac-datacenter/w/:slug', element: <AcArchive /> },
          { path: '/en/ac-datacenter/t/:slug', element: <AcCategory axis="type" /> },
          { path: '/en/ac-datacenter/a/:slug', element: <AcCategory axis="agent" /> },
          { path: '/en/ac-datacenter/i/:slug', element: <AcCategory axis="industry" /> },
          { path: '/en/ac-datacenter/i/:industry/t/:type', element: <AcCategory axis="cross" /> },
          { path: '/blog', element: <Blog /> },
          { path: '/blog/:slug', element: <BlogPost /> },
          { path: '/en/blog', element: <Blog /> },
          { path: '/en/blog/:slug', element: <BlogPost /> },
          { path: '/insurance-refund', element: <InsuranceRefund /> },
          { path: '/insurance-refund/:slug', element: <InsuranceRefundIndustry /> },
          { path: '/en/insurance-refund', element: <InsuranceRefund /> },
          { path: '/en/insurance-refund/:slug', element: <InsuranceRefundIndustry /> },
          // ── /features/* 9개 SEO 공개 랜딩 ─────────────────────────
          { path: '/features/jsa', element: <FeatureJSA /> },
          { path: '/features/tbm', element: <FeatureTBM /> },
          { path: '/features/safety-education', element: <FeatureSafetyEducation /> },
          { path: '/features/work-permit', element: <FeatureWorkPermit /> },
          { path: '/features/accident-report', element: <FeatureAccidentReport /> },
          { path: '/features/chatbot', element: <FeatureChatbot /> },
          { path: '/features/risk-assessment', element: <FeatureRiskAssessment /> },
          { path: '/features/image-analysis', element: <FeatureImageAnalysis /> },
          { path: '/features/corrective', element: <FeatureCorrective /> },
          { path: '/features/eta', element: <FeatureETA /> },
          { path: '/features/fta', element: <FeatureFTA /> },
          { path: '/features/safety-cost', element: <FeatureSafetyCost /> },
          { path: '/features/pnid-hazop', element: <FeaturePnidHazop /> },
          { path: '/newsletter', element: <Newsletter /> },
          { path: '/newsletter/verify', element: <NewsletterVerify /> },
          { path: '/newsletter/unsubscribe', element: <NewsletterUnsubscribe /> },
          // 미가입 하청 공개 초대 랜딩(로그인 불필요)
          { path: '/partner-invite/:token', element: <PartnerInviteLanding /> },
        ],
      },
      // 인증 페이지 (헤더/푸터 없음)
      {
        element: (
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        ),
        children: [
          { path: '/login', element: <Login /> },
          { path: '/signup', element: <Signup /> },
          { path: '/verify-email', element: <VerifyEmail /> },
          { path: '/auth/callback', element: <OAuthCallback /> },
        ],
      },
      // 보호된 대시보드 라우트 — 비로그인·이메일 미인증 처리
      {
        element: <ProtectedRouteGuard />,
        children: [
          // PSM 자체감사 고객사 포털 — 컨설팅 고객사 담당자용(Shell 없음, grant 기반)
          {
            element: (
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
            ),
            children: [
              { path: '/dashboard/psm-portal', element: <PsmPortalHome /> },
              { path: '/dashboard/psm-portal/audits/:auditId', element: <PsmPortalAudit /> },
            ],
          },
          // 일반 대시보드 (AppShell)
          {
            element: (
              <AppShell>
                <Suspense fallback={<PageLoader />}>
                  <Outlet />
                </Suspense>
              </AppShell>
            ),
            children: [
              { path: '/dashboard', element: <Dashboard /> },
              { path: '/dashboard/enterprise/custom-format', element: <CustomFormatRequest /> },
              { path: '/dashboard/safety-rule-match', element: <SafetyRuleMatch /> },
              { path: '/dashboard/safety-rule-match/ledger', element: <ComplianceLedger /> },
              { path: '/dashboard/safety-rule-match/check/:checkId', element: <ComplianceCheckDetail /> },
              { path: '/dashboard/safety-education/play/:sessionId', element: <SlideshowPlayer /> },
              { path: '/dashboard/safety-education/library', element: <LessonLibrary /> },
              { path: '/dashboard/usage', element: <Usage /> },
              { path: '/dashboard/records', element: <Records /> },
              { path: '/dashboard/chatbot', element: <Chatbot /> },
              { path: '/dashboard/chat-studio', element: <ChatStudio /> },
              { path: '/dashboard/integrated', element: <Integrated /> },
              { path: '/dashboard/jsa', element: <JSA /> },
              { path: '/dashboard/tbm', element: <TBM /> },
              { path: '/dashboard/image-analysis', element: <ImageAnalysis /> },
              // 현장 분석 — 통합 진입점. 기존 /corrective 라우트도 ImageAnalysis 페이지로 alias (시정지시서는 토글로 함께 생성).
              { path: '/dashboard/corrective', element: <ImageAnalysis /> },
              { path: '/dashboard/corrective-legacy', element: <Corrective /> },
              { path: '/dashboard/risk-assessment', element: <RiskAssessment /> },
              { path: '/dashboard/work-permit', element: <WorkPermit /> },
              { path: '/dashboard/work-timing', element: <WorkTiming /> },
              { path: '/dashboard/accident-report', element: <AccidentReport /> },
              { path: '/dashboard/safety-education', element: <SafetyEducation /> },
              { path: '/dashboard/safety-education/course', element: <SafetyEducationCourse /> },
              { path: '/dashboard/safety-education/:courseId/attendance', element: <CourseAttendanceSummary /> },
              { path: '/dashboard/safety-cost', element: <SafetyCost /> },
              { path: '/dashboard/hazard-area-classification', element: <HazardAreaClassification /> },
              { path: '/dashboard/equipment-cert', element: <EquipmentCert /> },
              { path: '/dashboard/safety-signs', element: <SafetySigns /> },
              { path: '/dashboard/pnid', element: <PnID /> },
              { path: '/dashboard/templates', element: <Templates /> },
              { path: '/dashboard/billing', element: <Subscription /> },
              { path: '/dashboard/billing/invoices', element: <Invoices /> },
              { path: '/dashboard/settings', element: <Settings /> },
              { path: '/dashboard/daily-brief', element: <DailyBriefToday /> },
              { path: '/dashboard/daily-brief/archive', element: <DailyBriefArchive /> },
              // 사업장 관리 통합 — 단수 /site (1계정 1사업장)
              { path: '/dashboard/today', element: <TodayTasks /> },
              { path: '/dashboard/onboarding', element: <OnboardingPage /> },
              { path: '/dashboard/site', element: <SiteEntry /> },
              { path: '/dashboard/site/new', element: <SiteWizard /> },
              { path: '/dashboard/site/:id', element: <SiteDetail /> },
              { path: '/dashboard/site/:id/edit', element: <SiteWizard /> },
              { path: '/dashboard/site/:id/import', element: <ImportHub /> },
              // Partner Safety OS — 협력사 관리(원청). AppShell(테마 준수) 안 + PartnersShell 서브탭.
              {
                path: '/dashboard/partners',
                element: <PartnersShell />,
                children: [
                  { index: true, element: <PartnersDashboard /> },
                  { path: 'duty', element: <PartnerDuty /> },
                  { path: 'tickets', element: <PartnerTickets /> },
                  { path: 'settings', element: <PartnerSettings /> },
                ],
              },
              // 드릴다운(서브탭 없이 전폭) — 협력사 상세·순회점검 위저드
              { path: '/dashboard/partners/:linkId', element: <PartnerDetail /> },
              { path: '/dashboard/partners/:linkId/inspect', element: <PartnerInspect /> },
              { path: '/dashboard/partner-invites', element: <InviteAccept /> },
              { path: '/dashboard/training/sessions/:id', element: <SessionDetail /> },
              // ETA/FTA 통합
              { path: '/dashboard/risk-analysis', element: <RiskAnalysisHub /> },
              // 구 라우트 호환 — 단수 형태로 리다이렉트
              { path: '/dashboard/sites', element: <Navigate to="/dashboard/site" replace /> },
              { path: '/dashboard/sites/new', element: <Navigate to="/dashboard/site/new" replace /> },
              { path: '/dashboard/compliance', element: <Navigate to="/dashboard/site" replace /> },
              { path: '/dashboard/training', element: <Navigate to="/dashboard/site" replace /> },
              { path: '/dashboard/eta', element: <Navigate to="/dashboard/risk-analysis?mode=eta" replace /> },
              { path: '/dashboard/fta', element: <Navigate to="/dashboard/risk-analysis?mode=fta" replace /> },
            ],
          },
          // 관리자 전용 — 별도 AdminLayout (slate-950 사이드바)
          {
            element: <AdminRoute />,
            children: [
              {
                path: '/dashboard/admin',
                element: <AdminLayout />,
                children: [
                  { index: true, element: <AdminDashboard /> },
                  { path: 'users', element: <AdminUsers /> },
                  { path: 'users/:id', element: <AdminUserDetail /> },
                  { path: 'subscriptions', element: <AdminSubscriptions /> },
                  { path: 'payments', element: <AdminPayments /> },
                  { path: 'activity', element: <AdminActivity /> },
                  { path: 'sites', element: <AdminSites /> },
                  { path: 'support', element: <AdminSupport /> },
                  { path: 'newsletter', element: <AdminNewsletter /> },
                  { path: 'audit', element: <AdminAudit /> },
                  { path: 'metrics', element: <AdminMetrics /> },
                  { path: 'content', element: <AdminContentMetrics /> },
                  { path: 'errors', element: <AdminErrorLogs /> },
                  { path: 'sapa', element: <AdminSapaAnalysis /> },
                ],
              },
            ],
          },
          // PSM 자체감사 — 컨설팅 전용 독립 작업공간.
          // ⚠️ AdminRoute(is_admin 강제) **밖**에 둔다: 관리자가 지정한 감사자(psm_auditor)는
          //    관리자 권한 없이도 접근해야 하기 때문. 가드는 PsmRoute(=서버 psm_access 와 동일 조건).
          {
            element: <PsmRoute />,
            children: [
              {
                path: '/dashboard/psm-audit',
                element: <PsmLayout />,
                children: [
                  { index: true, element: <AdminPsmAudit /> },
                  { path: 'clients', element: <AdminPsmClients /> },
                  { path: 'clients/:clientId', element: <AdminPsmClients /> },
                  // 감사자 팀(계정 단위 소속, 마이그 096). 편성은 대표만이지만 자기 팀
                  // 읽기는 누구나 가능하므로 라우트 자체는 PsmRoute 아래 그대로 둔다.
                  { path: 'teams', element: <PsmTeams /> },
                  { path: 'audits/:auditId', element: <AdminPsmAuditDetail /> },
                  // 태블릿 현장 채점 — 필기용 종이 서식의 전자 대체
                  { path: 'audits/:auditId/field', element: <PsmFieldScoreMode /> },
                  // 현장 촬영 — 순회하며 1장씩 즉시 업로드·분석(오프라인 큐)
                  { path: 'audits/:auditId/field-photo', element: <PsmFieldPhotoMode /> },
                  // 모의 이행상태평가 결과 — 환산점수·등급 구간·효율순 보완 권고(설계서 §5-6)
                  { path: 'audits/:auditId/grade', element: <PsmGradeResult /> },
                  // 파일철 단위 서류확인 세션(설계서 §5-2) — 철 목록 / 한 철의 세션
                  { path: 'audits/:auditId/filing', element: <PsmFilingSession /> },
                  { path: 'audits/:auditId/filing/:element', element: <PsmFilingSession /> },
                  // AI 면담(설계서 §6). 진행 화면은 태블릿을 피면담자에게 건네는 화면이지만
                  // **인증은 감사원 로그인 세션 그대로**다 — 무가입 토큰 링크는 §6-0 에서
                  // 폐기했다. 이탈 방어는 4자리 종료 PIN(서버 대조) + 전체화면.
                  { path: 'audits/:auditId/interview', element: <PsmInterviewPrep /> },
                  { path: 'audits/:auditId/interview/:sid/run', element: <PsmInterviewRun /> },
                  { path: 'audits/:auditId/interview/:sid/review', element: <PsmInterviewReview /> },
                  // 첫 사용 안내 재열람 + 첫 감사 체크리스트(P3-19.1/19.2)
                  { path: 'guide', element: <PsmGuide /> },
                  // 마감 체크(설계서 §5-5) — 흩어진 게이트 신호를 한 화면에 모은다
                  { path: 'audits/:auditId/closing', element: <PsmClosingCheck /> },
                ],
              },
            ],
          },
        ],
      },
      // 게스트 출석 페이지 (인증 없음, Shell 없음)
      {
        element: (
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        ),
        children: [
          { path: '/edu/:token', element: <GuestCheckIn /> },
          { path: '/report/:token', element: <SafetyReportSubmit /> },
          { path: '/psm-sign/:token', element: <PsmSignPage /> },
        ],
      },
      // 404
      {
        path: '*',
        element: (
          <Suspense fallback={<PageLoader />}>
            <NotFound />
          </Suspense>
        ),
      },
    ],
  },
])
